import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import AbandonCatcher from "../components/AbandonCatcher";
import { api, formatPrice } from "../lib/api";
import { ArrowLeft, ArrowUpRight, ShoppingBag, Zap } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { usePromo, applyDiscount } from "../components/PromoBanner";
import Reviews from "../components/Reviews";

const ProductDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const cart = useCart();
  const promo = usePromo();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        // fetch related by first category
        const cat = r.data.categories?.[0];
        if (cat) {
          api
            .get("/products", { params: { category: cat } })
            .then((rr) => setRelated(rr.data.filter((p) => p.id !== r.data.id).slice(0, 3)))
            .catch(() => setRelated([]));
        }
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24">
          <div className="aspect-[4/5] max-w-xl bg-[#1F1A17]" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-32 text-center">
          <h1 className="font-serif-display text-5xl text-[#F4EAD9]">Not found</h1>
          <p className="mt-4 text-sm text-[#B0A496]">This fragrance has slipped off the shelf.</p>
          <Link data-testid="back-to-shop" to="/shop" className="mt-8 inline-block editorial-link text-[11px] tracking-overline uppercase font-bold">
            Return to shop
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid="product-detail-page" className="min-h-screen">
      <Header />

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-10">
        <Link
          data-testid="back-link"
          to="/shop"
          className="inline-flex items-center gap-2 text-[11px] tracking-overline uppercase text-[#B0A496] hover:text-[#F4EAD9] font-bold"
        >
          <ArrowLeft size={14} strokeWidth={1.2} /> Back to catalog
        </Link>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-10 pb-24 md:pb-32 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-7">
          <div className="relative aspect-[4/5] overflow-hidden bg-[#1F1A17]">
            <img
              data-testid="product-detail-image"
              src={product.image_url}
              alt={product.name}
              className="w-full h-full object-cover"
            />
            {product.is_best_seller && (
              <span className="absolute top-5 left-5 bg-[#D4AF37] text-[#15110F] px-3 py-1 text-[10px] tracking-overline uppercase font-bold">
                Best Seller
              </span>
            )}
          </div>
        </div>

        <div className="md:col-span-5 md:pt-8">
          <p className="text-[11px] tracking-overline uppercase text-[#D4AF37] mb-5 font-bold">
            Eau de Parfum · 30ml
          </p>
          <h1
            data-testid="detail-name"
            className="font-serif-display font-light text-5xl md:text-6xl leading-[0.95] tracking-tight text-[#F4EAD9]"
          >
            {product.name}
          </h1>
          <p
            data-testid="detail-price"
            className="mt-6 text-2xl tabular-nums text-[#D4AF37] font-medium"
          >
            {(() => {
              if (promo?.active && promo.discount_pct > 0) {
                const d = applyDiscount(product.price_inr, promo.discount_pct);
                return (
                  <>
                    <span className="text-[#D4AF37]">₹{d.formattedDiscounted}</span>
                    <span className="ml-3 text-base text-[#B0A496] line-through">
                      {formatPrice(product.price_inr)}
                    </span>
                    <span className="ml-3 text-[10px] tracking-overline uppercase font-bold text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/40 px-2 py-1">
                      Save {promo.discount_pct}% · {promo.label}
                    </span>
                  </>
                );
              }
              return formatPrice(product.price_inr);
            })()}
          </p>
          {promo?.active && promo.type === "bogo" && (
            <p
              data-testid="detail-bogo-badge"
              className="mt-4 inline-flex items-center gap-2 border border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37] px-4 py-2 text-[11px] tracking-overline uppercase font-bold"
            >
              {promo.label} · Buy 1 Get 1 Free · Free shipping PAN India
            </p>
          )}

          <div className="mt-10 border-t border-[#2D2622] pt-8">
            <p className="text-[11px] tracking-overline uppercase text-[#D4AF37] mb-3 font-bold">Notes</p>
            <p data-testid="detail-notes" className="font-serif-display text-2xl leading-snug text-[#F4EAD9]">
              {product.notes}
            </p>
          </div>

          {product.description && (
            <div className="mt-10 border-t border-[#2D2622] pt-8">
              <p className="text-[11px] tracking-overline uppercase text-[#D4AF37] mb-3 font-bold">The story</p>
              <p data-testid="detail-description" className="text-base leading-relaxed text-[#B0A496]">
                {product.description}
              </p>
            </div>
          )}

          <div className="mt-10 border-t border-[#2D2622] pt-8">
            <p className="text-[11px] tracking-overline uppercase text-[#D4AF37] mb-4 font-bold">Wears well</p>
            <div className="flex flex-wrap gap-2">
              {product.categories.map((c) => (
                <Link
                  key={c}
                  to={`/shop?category=${encodeURIComponent(c)}`}
                  data-testid={`detail-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  className="border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#15110F] px-4 py-2 text-[10px] tracking-overline uppercase transition-colors font-bold"
                >
                  {c}
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-12 space-y-4">
            <div className="flex flex-wrap items-center gap-4">
              <button
                data-testid="add-to-cart-cta"
                onClick={() => {
                  cart.add(product, 1);
                  cart.setOpen(true);
                  toast.success("Added to cart", {
                    description: `${product.name} · ${formatPrice(product.price_inr)}`,
                  });
                }}
                className="inline-flex items-center gap-3 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#15110F] px-8 py-4 text-[11px] tracking-overline uppercase font-bold transition-colors"
              >
                <ShoppingBag size={16} strokeWidth={1.2} />
                Add to cart
              </button>

              <button
                data-testid="buy-now-cta"
                onClick={() => {
                  cart.add(product, 1);
                  nav("/checkout");
                }}
                className="inline-flex items-center gap-3 bg-[#D4AF37] hover:bg-[#B8941F] text-[#15110F] px-8 py-4 text-[11px] tracking-overline uppercase font-bold transition-colors"
              >
                <Zap size={16} strokeWidth={1.2} />
                Buy now — {formatPrice(product.price_inr)}
                <ArrowUpRight size={16} strokeWidth={1.2} />
              </button>
            </div>

            <Link
              data-testid="back-cta"
              to="/shop"
              className="editorial-link text-[11px] tracking-overline uppercase text-[#F4EAD9] font-bold"
            >
              Continue browsing
            </Link>
          </div>
        </div>
      </section>

      {product && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-12">
          <Reviews productId={product.id} />
        </section>
      )}

      {related.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-32">
          <p className="text-[11px] tracking-overline uppercase text-[#D4AF37] mb-4 font-bold">
            You may also wear
          </p>
          <h2 className="font-serif-display font-light text-3xl md:text-5xl leading-tight text-[#F4EAD9] mb-12">
            From the same <span className="italic">family</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
            {related.map((p, idx) => (
              <Link
                key={p.id}
                to={`/product/${p.id}`}
                data-testid={`related-${p.id}`}
                className="image-zoom-parent group"
              >
                <div className="aspect-[4/5] overflow-hidden bg-[#1F1A17]">
                  <img src={p.image_url} alt={p.name} className="image-zoom w-full h-full object-cover" />
                </div>
                <div className="mt-5 flex justify-between gap-4">
                  <h3 className="font-serif-display text-2xl text-[#F4EAD9]">{p.name}</h3>
                  <p className="text-base tabular-nums">{formatPrice(p.price_inr)}</p>
                </div>
                <p className="mt-1 text-sm text-[#D4AF37]">{p.notes}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      <Footer />
      {product && <AbandonCatcher productId={product.id} productName={product.name} />}
    </div>
  );
};

export default ProductDetail;
